import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { workAuthService } from '@/services/workAuthService';
import type {
  WorkAuthResponse,
  DocumentResponse,
  DashboardStats,
  QueryWorkAuth,
  ReviewDocument,
  ApproveWorkAuth,
  WorkAuthStatus,
  DocumentReviewStatus
} from '@shared/schemas/workAuth';

interface WorkAuthState {
  // Work Authorizations
  workAuths: WorkAuthResponse[];
  selectedWorkAuth: WorkAuthResponse | null;
  workAuthsLoading: boolean;
  workAuthsError: string | null;

  // Documents
  documents: DocumentResponse[];
  pendingReviewDocuments: DocumentResponse[];
  documentsLoading: boolean;
  documentsError: string | null;

  // Dashboard Stats
  stats: DashboardStats | null;
  statsLoading: boolean;
  statsError: string | null;

  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Filters
  filters: {
    status?: WorkAuthStatus;
    expiringWithinDays?: number;
    search?: string;
  };

  // Sync Status
  syncStatus: {
    isScheduled: boolean;
    isRunning: boolean;
    lastRun: string;
    nextRun: string;
  } | null;
}

const initialState: WorkAuthState = {
  workAuths: [],
  selectedWorkAuth: null,
  workAuthsLoading: false,
  workAuthsError: null,

  documents: [],
  pendingReviewDocuments: [],
  documentsLoading: false,
  documentsError: null,

  stats: null,
  statsLoading: false,
  statsError: null,

  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },

  filters: {},

  syncStatus: null
};

/**
 * Fetch work authorizations with filters
 */
export const fetchWorkAuthorizations = createAsyncThunk(
  'workAuth/fetchWorkAuthorizations',
  async (params: QueryWorkAuth, { rejectWithValue }) => {
    try {
      console.log('🚀 fetchWorkAuthorizations thunk called with params:', params);
      const response = await workAuthService.getWorkAuthorizations(params);
      console.log('🚀 fetchWorkAuthorizations service returned:', response);
      return response;
    } catch (error: any) {
      console.error('🚀 fetchWorkAuthorizations error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch work authorizations');
    }
  }
);

/**
 * Fetch single work authorization by ID
 */
export const fetchWorkAuthorizationById = createAsyncThunk(
  'workAuth/fetchWorkAuthorizationById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await workAuthService.getWorkAuthorizationById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch work authorization');
    }
  }
);

/**
 * Fetch work authorization by employee ID
 */
export const fetchWorkAuthByEmployeeId = createAsyncThunk(
  'workAuth/fetchWorkAuthByEmployeeId',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      const response = await workAuthService.getWorkAuthByEmployeeId(employeeId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch work authorization');
    }
  }
);

/**
 * Upload documents
 */
export const uploadDocuments = createAsyncThunk(
  'workAuth/uploadDocuments',
  async (data: { workAuthId: string; documentType: string; file: File; submittedBy: string }, { rejectWithValue }) => {
    try {
      const response = await workAuthService.uploadDocuments(
        data.workAuthId,
        data.documentType,
        data.file,
        data.submittedBy
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload document');
    }
  }
);

/**
 * Review document
 */
export const reviewDocument = createAsyncThunk(
  'workAuth/reviewDocument',
  async (data: { documentId: string; reviewData: ReviewDocument }, { rejectWithValue }) => {
    try {
      const response = await workAuthService.reviewDocument(data.documentId, data.reviewData);
      return response;
    } catch (error: any) {
      console.error('reviewDocument thunk error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to review document';
      console.error('Rejecting with value:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Approve work authorization (set temporary/permanent status)
 */
export const approveWorkAuthorization = createAsyncThunk(
  'workAuth/approveWorkAuthorization',
  async (data: { id: string; approvalData: ApproveWorkAuth }, { rejectWithValue }) => {
    try {
      const response = await workAuthService.approveWorkAuthorization(data.id, data.approvalData);
      return response;
    } catch (error: any) {
      console.error('approveWorkAuthorization thunk error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to approve work authorization';
      console.error('Rejecting with value:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetch documents pending review
 */
export const fetchPendingReviewDocuments = createAsyncThunk(
  'workAuth/fetchPendingReviewDocuments',
  async (params: { status?: DocumentReviewStatus; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await workAuthService.getDocumentsForReview(params);
      console.log('📋 Thunk: response from service =', response);
      console.log('📋 Thunk: returning =', response);
      return response;
    } catch (error: any) {
      console.error('❌ Thunk error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending documents');
    }
  }
);

/**
 * Fetch dashboard statistics
 */
export const fetchDashboardStats = createAsyncThunk(
  'workAuth/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workAuthService.getDashboardStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard stats');
    }
  }
);

/**
 * Terminate work authorization
 */
export const terminateWorkAuth = createAsyncThunk(
  'workAuth/terminateWorkAuth',
  async (data: { id: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await workAuthService.terminateWorkAuth(data.id, data.reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to terminate work authorization');
    }
  }
);

/**
 * Import from CSV
 */
export const importFromCSV = createAsyncThunk(
  'workAuth/importFromCSV',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await workAuthService.importFromCSV(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to import CSV');
    }
  }
);

/**
 * Export to CSV
 */
export const exportToCSV = createAsyncThunk(
  'workAuth/exportToCSV',
  async (_, { rejectWithValue }) => {
    try {
      await workAuthService.exportToCSV();
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export CSV');
    }
  }
);

/**
 * Trigger manual sync
 */
export const triggerManualSync = createAsyncThunk(
  'workAuth/triggerManualSync',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workAuthService.triggerManualSync();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to trigger sync');
    }
  }
);

/**
 * Fetch sync status
 */
export const fetchSyncStatus = createAsyncThunk(
  'workAuth/fetchSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workAuthService.getSyncStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sync status');
    }
  }
);

const workAuthSlice = createSlice({
  name: 'workAuth',
  initialState,
  reducers: {
    // Clear errors
    clearWorkAuthErrors: (state) => {
      state.workAuthsError = null;
      state.documentsError = null;
      state.statsError = null;
    },

    // Update filters
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
    },

    // Set selected work auth
    setSelectedWorkAuth: (state, action: PayloadAction<WorkAuthResponse | null>) => {
      state.selectedWorkAuth = action.payload;
    },

    // Clear selected work auth
    clearSelectedWorkAuth: (state) => {
      state.selectedWorkAuth = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch work authorizations
    builder
      .addCase(fetchWorkAuthorizations.pending, (state) => {
        state.workAuthsLoading = true;
        state.workAuthsError = null;
      })
      .addCase(fetchWorkAuthorizations.fulfilled, (state, action) => {
        state.workAuthsLoading = false;
        console.log('📦 fetchWorkAuthorizations.fulfilled action.payload:', action.payload);
        console.log('📦 action.payload.data:', action.payload.data);
        state.workAuths = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        console.log('📦 state.workAuths after:', state.workAuths);
      })
      .addCase(fetchWorkAuthorizations.rejected, (state, action) => {
        state.workAuthsLoading = false;
        state.workAuthsError = action.payload as string;
      });

    // Fetch work authorization by ID
    builder
      .addCase(fetchWorkAuthorizationById.pending, (state) => {
        state.workAuthsLoading = true;
        state.workAuthsError = null;
      })
      .addCase(fetchWorkAuthorizationById.fulfilled, (state, action) => {
        state.workAuthsLoading = false;
        state.selectedWorkAuth = action.payload.data;
      })
      .addCase(fetchWorkAuthorizationById.rejected, (state, action) => {
        state.workAuthsLoading = false;
        state.workAuthsError = action.payload as string;
      });

    // Fetch work authorization by employee ID
    builder
      .addCase(fetchWorkAuthByEmployeeId.fulfilled, (state, action) => {
        state.selectedWorkAuth = action.payload.data;
      });

    // Upload documents
    builder
      .addCase(uploadDocuments.pending, (state) => {
        state.documentsLoading = true;
        state.documentsError = null;
      })
      .addCase(uploadDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        state.documents.unshift(action.payload);
      })
      .addCase(uploadDocuments.rejected, (state, action) => {
        state.documentsLoading = false;
        state.documentsError = action.payload as string;
      });

    // Review document
    builder
      .addCase(reviewDocument.pending, (state) => {
        state.documentsLoading = true;
        state.documentsError = null;
      })
      .addCase(reviewDocument.fulfilled, (state, action) => {
        state.documentsLoading = false;
        // Update document in pending review list
        state.pendingReviewDocuments = state.pendingReviewDocuments.filter(
          doc => doc.id !== action.payload.id
        );
      })
      .addCase(reviewDocument.rejected, (state, action) => {
        state.documentsLoading = false;
        state.documentsError = action.payload as string;
      });

    // Approve work authorization
    builder
      .addCase(approveWorkAuthorization.pending, (state) => {
        state.workAuthsLoading = true;
        state.workAuthsError = null;
      })
      .addCase(approveWorkAuthorization.fulfilled, (state, action) => {
        state.workAuthsLoading = false;
        const workAuth = action.payload.data;
        // Update the selected work auth with approval data
        if (state.selectedWorkAuth && state.selectedWorkAuth.id === workAuth.id) {
          state.selectedWorkAuth = workAuth;
        }
        // Update work auth in list
        state.workAuths = state.workAuths.map(wa =>
          wa.id === workAuth.id ? workAuth : wa
        );
      })
      .addCase(approveWorkAuthorization.rejected, (state, action) => {
        state.workAuthsLoading = false;
        state.workAuthsError = action.payload as string;
      });

    // Fetch pending review documents
    builder
      .addCase(fetchPendingReviewDocuments.pending, (state) => {
        state.documentsLoading = true;
        state.documentsError = null;
      })
      .addCase(fetchPendingReviewDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        console.log('📋 Reducer: action.payload =', action.payload);
        console.log('📋 Reducer: action.payload?.data =', action.payload?.data);
        console.log('📋 Reducer: action.payload?.pagination =', action.payload?.pagination);
        state.pendingReviewDocuments = action.payload?.data || [];
        state.pagination = action.payload?.pagination || state.pagination;
        console.log('📋 Reducer: final state.pendingReviewDocuments =', state.pendingReviewDocuments);
      })
      .addCase(fetchPendingReviewDocuments.rejected, (state, action) => {
        state.documentsLoading = false;
        state.documentsError = action.payload as string;
        state.pendingReviewDocuments = []; // Reset to empty array on error
      });

    // Fetch dashboard stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload as string;
      });

    // Terminate work authorization
    builder
      .addCase(terminateWorkAuth.fulfilled, (state, action) => {
        const workAuth = action.payload.data;
        // Update work auth in list
        state.workAuths = state.workAuths.map(wa =>
          wa.id === workAuth.id ? workAuth : wa
        );
        if (state.selectedWorkAuth?.id === workAuth.id) {
          state.selectedWorkAuth = workAuth;
        }
      });

    // Fetch sync status
    builder
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.syncStatus = action.payload;
      });
  }
});

export const {
  clearWorkAuthErrors,
  setFilters,
  clearFilters,
  setSelectedWorkAuth,
  clearSelectedWorkAuth
} = workAuthSlice.actions;

export default workAuthSlice.reducer;